// src\themes\lino\components\ThirdGridLine.tsx
'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import { loadThemeComponent } from '@/core/config/theme-component-loader';
import { textGroupVariants, textItemVariants } from './animations/textReveal';
const CustomButton = loadThemeComponent('CustomButton');

interface ImageInfo {
    url: string;
}

interface GridItem {
    title?: string | null;
    buttonContent?: string | null;
    link?: string | null;
    image: ImageInfo;
}

interface ThirdGridLineProps {
    data?: {
        title?: string | null;
        link?: string | null;
        buttonContent?: string | null;
        items?: GridItem[];
    };
}

export default function ThirdGridLine({ data }: ThirdGridLineProps) {
    if (!data || !data.items || data.items.length < 3) return null;

    const items = data.items.slice(0, 3);

    return (
        <div className="col-span-12 space-y-10">
            {(data.title || data.buttonContent) && (
                <motion.div
                    className="flex items-end justify-between border-b border-primary-200 pb-5"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.4 }}
                    variants={textGroupVariants}
                >
                    {data.title && (
                        <motion.h2 variants={textItemVariants} className="text-xl lg:text-2xl font-light text-primary-900 tracking-tight">{data.title}</motion.h2>
                    )}
                    {data.buttonContent && data.link && (
                        <motion.div variants={textItemVariants}>
                            <CustomButton asChild variant="outline" size="sm">
                                <Link href={data.link}>{data.buttonContent}</Link>
                            </CustomButton>
                        </motion.div>
                    )}
                </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {items.map((item, index) => {
                    const content = (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                            className="relative w-full aspect-[4/5] overflow-hidden rounded-2xl bg-primary-100 group border border-transparent hover:border-primary-200 transition-colors duration-500"
                        >
                            {item.image?.url && (
                                <Image
                                    src={item.image.url}
                                    alt={item.title || 'image'}
                                    fill
                                    className="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.035]"
                                />
                            )}
                            {(item.title || item.buttonContent) && (
                                <motion.div
                                    className="absolute bottom-0 inset-x-0 space-y-1.5 p-5 flex flex-col items-start text-primary-0 z-10 bg-gradient-to-t from-primary-950/60 to-transparent"
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: false, amount: 0.4 }}
                                    variants={textGroupVariants}
                                >
                                    {item.title && (
                                        <motion.span variants={textItemVariants} className="text-primary-0 text-sm tracking-wide">{item.title}</motion.span>
                                    )}
                                    {item.buttonContent && (
                                        <motion.span variants={textItemVariants} className="inline-block border-b border-primary-0/70 text-[11px] uppercase tracking-[0.12em] pb-0.5">
                                            {item.buttonContent}
                                        </motion.span>
                                    )}
                                </motion.div>
                            )}
                        </motion.div>
                    );

                    return (
                        <div key={index}>
                            {item.link ? <Link href={item.link}>{content}</Link> : content}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}